// SourcingProjectSupplierWidget.tsx
import { Button, Dialog } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { Restricted } from '@procureai/permissions-ui';
import { Operator } from '@procureai/query-builder';
import { Widget, WidgetContent, WidgetFooter } from '@procureai/ui-mui';
import { isNotEmpty } from 'ramda';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SupplierFilterField } from '@/generated/gql/graphql';
import { BaseDomainType, I18nNamespace } from '~/common/types';
import { SourcingProjectSupplierSegment } from '~/common/types/cubes/SourcingProjectSupplier';
import { EncodedQuery } from '~/components/EncodedQuery/EncodedQuery';
import { VirtualList, VirtualListProps } from '~/components/VirtualList/VirtualList';
import { useOpenClose } from '~/hooks';
import { RouteId } from '~/routes';

import { SourcingProjectSupplierWidgetHeader } from './components/SourcingProjectSupplierWidgetHeader/SourcingProjectSupplierWidgetHeader';
import { SupplierList } from './components/SupplierList/SupplierList';
import {
  SOURCING_PROJECT_SUPPLIER_LIST_ITEM_HEIGHT,
  SupplierListItem,
} from './components/SupplierListItem/SupplierListItem';
import { useSourcingProjectSupplier } from './SourcingProjectSupplierWidget.hooks';

const LIST_PREVIEW_LIMIT = 3;

export const SourcingProjectSupplierWidget = () => {
  const { t } = useTranslation(I18nNamespace.base, {
    keyPrefix: 'SourcingProjectSupplierWidget',
  });
  const { loading, sourcingProjectSuppliers, total, hasMore, query, segmentMapper, supplierIds } =
    useSourcingProjectSupplier({
      limit: LIST_PREVIEW_LIMIT,
    });

  const [dialogProps, dialogOpen] = useOpenClose();

  const renderItem: VirtualListProps<SourcingProjectSupplierSegment>['renderItem'] = useMemo(
    () =>
      ({ item }) => <SupplierListItem {...item} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const hasItems = !!total;

  const rules = useMemo(() => {
    return [{ field: SupplierFilterField.id, value: supplierIds, operator: Operator.INEQ }];
  }, [supplierIds]);
  return (
    <>
      <Widget>
        <SourcingProjectSupplierWidgetHeader title={t('title')} loading={loading} />
        <WidgetContent>
          {isNotEmpty(sourcingProjectSuppliers) && !loading ? (
            <SupplierList sourcingProjectSuppliers={sourcingProjectSuppliers} hasMore={hasMore} />
          ) : null}
        </WidgetContent>
        <WidgetFooter>
          <Button variant="text" disabled={!hasMore} onClick={dialogOpen}>
            {t('showAll', { count: total })}
          </Button>
          <Restricted needs={{ permission: Permission.BaseSupplierList }}>
            <EncodedQuery pathname={RouteId.analyticsBase(BaseDomainType.SUPPLIER)} rules={rules}>
              {(linkProps) => (
                <Button variant="text" disabled={!hasItems} {...linkProps}>
                  {t('showInBase')}
                </Button>
              )}
            </EncodedQuery>
          </Restricted>
        </WidgetFooter>
      </Widget>
      <Dialog {...dialogProps}>
        <SourcingProjectSupplierWidgetHeader title={t('title')} />
        <VirtualList
          // height of a rendered item with name, location and proposal count
          itemSize={SOURCING_PROJECT_SUPPLIER_LIST_ITEM_HEIGHT}
          query={query}
          queryOptions={{
            castNumerics: true,
            skip: !dialogProps.open,
            segmentMapper,
          }}
          // Lack of generic type forwarding in VirtualListProps requires `as any`
          renderItem={renderItem as any}
        />
      </Dialog>
    </>
  );
};
